import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuerySample } from '@store/helpers';
import { selectUsers } from '@store/slices/users/usersSlice';

// Activity dashboard: counts only (HIPAA), no member names, ids, addresses or ride details.
type ActivityDashboardBody = { period: string };

// comparison: "7 more", "Same as prior", "0.2 pts lower"
type ActivitySummary = { startDate: string; endDate: string } & Record<
  'ridesCreated' | 'ridesCanceled' | 'membersCreated' | 'cancelRatePercent',
  { value: number | null; priorValue: number | null; comparison: string }
>;

// label: YYYY-MM-DD
type ActivityRidesPoint = {
  label: string;
  createdSegments: number;
  canceledSegments: number;
};

type ActivityRecordRow = {
  recordType: string;
  label: string;
  created: number;
  modified: number;
  // null: deactivated, not canceled (members, contractors, users)
  canceled: number | null;
  total: number;
};

export const globalApi = createApi({
  reducerPath: 'Global',
  tagTypes: ['Global'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    createOrUpdateUserContacts: builder.mutation({
      query: ({ id, path, body }) => ({
        url: `/v1/places/contacts/${id ? `update/${id}/${path}` : `create/${path}`}`,
        method: id ? 'PUT' : 'POST',
        body,
      }),
    }),

    attachUserContacts: builder.query({
      query: ({ id, module }) => ({
        url: `/v1/places/contacts/${module}/attach/${id}`,
      }),
    }),

    getUserContacts: builder.mutation({
      query: ({ argument: { pathName }, body = {}, params = {} }) => ({
        url: `/v1/places/contacts/authorized/${pathName}`,
        method: 'POST',
        body,
        params: {
          sortBy: 'firstName',
          sortDir: 'desc',
          page: 0,
          size: 7,
          ...params,
        },
      }),
    }),
    getUserContactById: builder.query({
      query: ({ id }) => ({
        url: `/v1/places/contacts/${id}`,
      }),
    }),
    uploadImage: builder.mutation({
      query: ({ body, url, method }) => {
        return {
          url,
          method,
          body,
        };
      },
    }),
    createOrUpdateLocation: builder.mutation({
      query: ({
        type,
        body,
        id,
      }:
        | { type: 'create'; id?: never; body: any }
        | { type: 'update'; id: number | string; body: any }) => ({
        url:
          type === 'create'
            ? `/v1/places/location/create`
            : `/v1/places/location/update/${id}`,
        method: type === 'create' ? 'POST' : 'PUT',
        body,
      }),
    }),
    getMemberModalsContactSelect: builder.query({
      query: () => ({
        // url: `/v1/places/contacts/member/authorized`,
        url: `/v1/places/contacts/member/contacts`,
      }),
    }),

    getRecentActivity: builder.query<any, any>({
      queryFn: async ({ params = {} } = {}, { getState }, _, baseQuery) => {
        const { isContractorVersion } = selectUsers(getState());

        return baseQuery({
          url: `/${isContractorVersion ? 'v2' : 'v1'}/logs/modification`,
          params: {
            sortBy: 'actionTime',
            sortDir: 'desc',
            page: 0,
            size: 7,
            ...params,
          },
        });
      },
    }),
    getActivitySummary: builder.query<ActivitySummary, ActivityDashboardBody>({
      query: (body) => ({
        url: `/v1/logs/activity-dashboard/summary`,
        method: 'POST',
        body,
      }),
    }),
    getActivityRidesByPoints: builder.query<
      ActivityRidesPoint[],
      ActivityDashboardBody
    >({
      query: (body) => ({
        url: `/v1/logs/activity-dashboard/rides-by-points`,
        method: 'POST',
        body,
      }),
    }),
    getActivityByRecords: builder.query<
      { rows: ActivityRecordRow[]; allRecords: ActivityRecordRow },
      ActivityDashboardBody
    >({
      query: (body) => ({
        url: `/v1/logs/activity-dashboard/activity-by-records`,
        method: 'POST',
        body,
      }),
    }),
    getSegmentTransmit: builder.mutation({
      query: ({ params = {}, argument: { id }, body = {} }) => ({
        url: `/v1/logs/modification/segment/${id}`,
        method: 'POST',
        body,
        params: {
          sortBy: 'actionTime',
          sortDir: 'desc',
          page: 0,
          size: 5,
          ...params,
        },
      }),
    }),
  }),
});

export const {
  useCreateOrUpdateUserContactsMutation,
  useLazyAttachUserContactsQuery,
  useGetUserContactsMutation,
  useLazyGetUserContactByIdQuery,
  useUploadImageMutation,
  useCreateOrUpdateLocationMutation,
  useLazyGetMemberModalsContactSelectQuery,
  useLazyGetRecentActivityQuery,
  useGetActivitySummaryQuery,
  useGetActivityRidesByPointsQuery,
  useGetActivityByRecordsQuery,
  useGetSegmentTransmitMutation,
} = globalApi;
